import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api, ApiFailure } from "../api/client";
import type { Exclusion, OutboundPolicy, TenantLimits } from "../api/types";
import { useSession } from "../store/session";
import { DataTable } from "../components/data-table";
import { Tabs } from "../components/Tabs";
import type { DataTableRowAction } from "../components/data-table";
import {
  Badge, Button, Card, EmptyState, ErrorState, Field, Loading, PageHeader,
  inputClass,
} from "../components/ui";
import { formatDateTime } from "./Calls";

// Límites, antifraude y lista de no-llamar.
//
// La edición exige `limits.manage` y la lectura `limits.read`. Los campos se
// deshabilitan en lugar de esconderse cuando no hay permiso: ver qué límites
// tiene su cuenta es útil incluso para quien no puede cambiarlos, y esconderlos
// solo genera la pregunta.
export function Limits() {
  const can = useSession((s) => s.can);
  const editable = can("limits.manage");

  const [limits, setLimits] = useState<TenantLimits | null>(null);
  const [exclusions, setExclusions] = useState<Exclusion[]>([]);
  const [error, setError] = useState<string | null>(null);

  // El borrador de la política vive aquí y no dentro de una pestaña: los
  // límites de salida y la vigilancia antifraude son el mismo recurso partido
  // en dos secciones, y un borrador por pestaña perdería lo escrito al
  // cambiar de una a otra.
  const [policyID, setPolicyID] = useState<string | null>(null);
  const [draft, setDraft] = useState<OutboundPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [data, list] = await Promise.all([
        api.get<TenantLimits>("/api/v1/limits"),
        api.get<Exclusion[]>("/api/v1/exclusions"),
      ]);
      setLimits(data);
      setExclusions(list ?? []);
      setPolicyID((actual) => actual ?? data.policies[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const politica = limits?.policies.find((p) => p.id === policyID) ?? null;

  // El borrador se rearma cuando cambia la política guardada —otra troncal, o
  // una recarga tras guardar—, no en cada render.
  useEffect(() => {
    setDraft(politica);
  }, [politica]);

  if (limits === null && error === null) return <Loading />;
  if (limits === null) return <ErrorState message={error ?? ""} onRetry={() => void load()} />;

  const patch = politica !== null && draft !== null ? diferencias(draft, politica) : {};
  const pendientes = Object.keys(patch).length;

  async function guardar() {
    if (politica === null || pendientes === 0) return;
    setSaveError(null);
    setSaving(true);
    try {
      // Solo lo que cambió: el endpoint aplica encima de lo actual, y mandar
      // el objeto entero haría que dos personas editando a la vez se
      // sobrescribieran campos que ninguna tocó.
      await api.patch(`/api/v1/limits/${politica.id}`, patch);
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiFailure ? err.error.message : "no se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Límites y antifraude"
        description="Lo que la cuenta puede marcar, cuándo y cuánto."
      />

      {/* Fuera de las pestañas a propósito: una alarma detrás de una pestaña
          cerrada no existe. */}
      {limits.kill_switch && (
        <Card className="mb-4 border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
          <strong>Originación cortada.</strong> El interruptor de emergencia está
          encendido: no sale ninguna llamada nueva.
          {limits.suspended_reason && <> Motivo: {limits.suspended_reason}.</>}{" "}
          Solo la plataforma puede reactivar la cuenta.
        </Card>
      )}

      {limits.policies.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {limits.policies.map((p) => (
            <button
              key={p.id}
              onClick={() => setPolicyID(p.id)}
              className={
                "rounded-lg border px-3 py-1.5 text-sm transition " +
                (p.id === policyID
                  ? "border-sky-400/60 bg-sky-500/10 text-white"
                  : "border-white/10 text-slate-400 hover:bg-white/5")
              }
            >
              {p.trunk_name}
            </button>
          ))}
        </div>
      )}

      <Tabs
        param="seccion"
        tabs={[
          {
            id: "resumen",
            label: "Resumen",
            content: () => (
              <Resumen
                limits={limits}
                politica={politica}
                editable={editable}
                onSaved={() => void load()}
              />
            ),
          },
          {
            id: "salida",
            label: "Salida",
            content: () =>
              draft === null ? (
                <SinPolitica />
              ) : (
                <SeccionSalida
                  draft={draft}
                  setDraft={setDraft}
                  editable={editable}
                  timezone={limits.timezone}
                />
              ),
          },
          {
            id: "antifraude",
            label: "Antifraude",
            content: () =>
              draft === null ? (
                <SinPolitica />
              ) : (
                <SeccionAntifraude draft={draft} setDraft={setDraft} editable={editable} />
              ),
          },
          {
            id: "exclusiones",
            label: "Lista de no-llamar",
            badge: exclusions.length,
            content: () => (
              <ExclusionList
                exclusions={exclusions}
                editable={editable}
                onChanged={() => void load()}
              />
            ),
          },
        ]}
      />

      {/* La barra vive fuera de las pestañas: si estuviera dentro, cambiar de
          sección escondería que hay trabajo sin guardar. */}
      {editable && pendientes > 0 && (
        <div className="sticky bottom-4 z-20 mt-6">
          <Card className="flex flex-wrap items-center justify-between gap-3 border-sky-400/40 bg-[#131a33] p-3 shadow-xl">
            <span className="text-sm text-slate-200">
              {pendientes} {pendientes === 1 ? "cambio" : "cambios"} sin guardar
              {saveError !== null && (
                <span className="ml-2 text-rose-300">· {saveError}</span>
              )}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={saving} onClick={() => setDraft(politica)}>
                Descartar
              </Button>
              <Button disabled={saving} onClick={() => void guardar()}>
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/** diferencias arma el PATCH: solo los campos que el borrador cambió. */
function diferencias(draft: OutboundPolicy, original: OutboundPolicy): Record<string, unknown> {
  const campos = [
    "max_cps", "max_concurrent", "max_call_seconds", "allow_international",
    "allowed_from_minute", "allowed_to_minute", "on_anomaly", "max_failure_percent",
    "spike_factor",
  ] as const;

  const patch: Record<string, unknown> = {};
  for (const campo of campos) {
    if (draft[campo] !== original[campo]) patch[campo] = draft[campo];
  }
  return patch;
}

function SinPolitica() {
  return (
    <EmptyState
      title="Sin política de salida"
      description="La cuenta no tiene troncal asignada, así que no puede originar llamadas. La asigna la plataforma."
    />
  );
}

/** Resumen: el estado de la cuenta y lo que hoy está vigente. */
function Resumen({
  limits,
  politica,
  editable,
  onSaved,
}: {
  limits: TenantLimits;
  politica: OutboundPolicy | null;
  editable: boolean;
  onSaved: () => void;
}) {
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Cuenta</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <Detail label="Estado" value={<StatusBadge status={limits.status} />} />
          <Detail label="Zona horaria" value={limits.timezone} />
          <Detail
            label="Originación"
            value={
              limits.kill_switch ? (
                <Badge tone="bad">Cortada</Badge>
              ) : (
                <Badge tone="good">Permitida</Badge>
              )
            }
          />
        </dl>
        <p className="mt-3 text-xs text-slate-500">
          La zona horaria es la que se usa para el horario permitido y para el límite de
          gasto diario: «diario» es diario donde está la cuenta, no en UTC.
        </p>
      </Card>

      {politica !== null && (
        <section>
          <h2 className="mb-2 text-sm font-medium text-slate-100">
            Vigente en la troncal {politica.trunk_name}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Cifra valor={politica.max_cps} etiqueta="llamadas por segundo" />
            <Cifra valor={politica.max_concurrent} etiqueta="simultáneas" />
            <Cifra
              valor={Math.round(politica.max_call_seconds / 60)}
              etiqueta="minutos por llamada"
            />
            <Cifra
              valor={politica.allow_international ? "sí" : "no"}
              etiqueta="internacional"
            />
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Horario permitido:{" "}
            {politica.allowed_from_minute === politica.allowed_to_minute ? (
              "sin restricción"
            ) : (
              <>
                de {minutesToTime(politica.allowed_from_minute)} a{" "}
                {minutesToTime(politica.allowed_to_minute)} ({limits.timezone})
              </>
            )}
            {" · "}
            Al detectar una anomalía:{" "}
            {politica.on_anomaly === "suspend" ? "avisar y suspender" : "avisar"}
          </p>
        </section>
      )}

      <Retencion limits={limits} editable={editable} onSaved={onSaved} />
    </div>
  );
}

/** Retención: cuánto se conserva cada cosa, y por fin editable.
 *
 * Antes esta tarjeta solo mostraba los cuatro plazos y decía «escríbenos para
 * cambiarlos», porque era verdad: no existía ninguna ruta que los escribiera y
 * la única forma era entrar a la base.
 *
 * `0` significa sin límite y se dice con palabras, no solo con el número: un
 * cero a secas se lee con facilidad como «no se guarda nada».
 */
function Retencion({
  limits,
  editable,
  onSaved,
}: {
  limits: TenantLimits;
  editable: boolean;
  onSaved: () => void;
}) {
  const actual = useMemo(
    () => ({
      cdr_retention_days: limits.cdr_retention_days,
      stream_retention_days: limits.stream_retention_days,
      event_retention_days: limits.event_retention_days,
      recording_retention_days: limits.recording_retention_days,
    }),
    [limits],
  );

  const [draft, setDraft] = useState(actual);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(actual);
  }, [actual]);

  const patch: Record<string, number> = {};
  for (const [campo, valor] of Object.entries(draft)) {
    if (valor !== actual[campo as keyof typeof actual]) patch[campo] = valor;
  }
  const pendientes = Object.keys(patch).length;

  async function guardar() {
    if (pendientes === 0) return;
    setError(null);
    setSaving(true);
    try {
      await api.patch("/api/v1/limits", patch);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  const campos = [
    { clave: "cdr_retention_days", etiqueta: "Llamadas" },
    { clave: "stream_retention_days", etiqueta: "Streams" },
    { clave: "event_retention_days", etiqueta: "Eventos" },
    { clave: "recording_retention_days", etiqueta: "Grabaciones" },
  ] as const;

  return (
    <Card className="p-4">
      <h2 className="text-sm font-medium text-slate-100">Retención de datos</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-4">
        {campos.map(({ clave, etiqueta }) => (
          <Field key={clave} label={`${etiqueta} (días)`} hint={retention(draft[clave])}>
            <input
              type="number"
              min={0}
              max={36500}
              className={inputClass}
              disabled={!editable || saving}
              value={draft[clave]}
              onChange={(e) =>
                setDraft({ ...draft, [clave]: Math.max(0, Number(e.target.value) || 0) })
              }
            />
          </Field>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        <strong>0 = sin límite</strong>: no se purga nada por antigüedad. El audio ocupa mucho
        más que el registro de la llamada, por eso su plazo es independiente.
      </p>
      {error !== null && <p className="mt-2 text-xs text-rose-300">{error}</p>}
      {editable && (
        <div className="mt-3 flex items-center gap-3">
          <Button onClick={() => void guardar()} disabled={pendientes === 0 || saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
          {pendientes > 0 && (
            <span className="text-xs text-slate-400">
              {pendientes} {pendientes === 1 ? "cambio" : "cambios"} sin guardar
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

function Cifra({ valor, etiqueta }: { valor: number | string; etiqueta: string }) {
  return (
    <Card className="p-4">
      <p className="text-2xl font-semibold tabular-nums text-slate-100">{valor}</p>
      <p className="mt-0.5 text-xs text-slate-500">{etiqueta}</p>
    </Card>
  );
}

/** SeccionSalida: cuánto y cuándo puede marcar la cuenta. */
function SeccionSalida({
  draft,
  setDraft,
  editable,
  timezone,
}: {
  draft: OutboundPolicy;
  setDraft: (p: OutboundPolicy) => void;
  editable: boolean;
  timezone: string;
}) {
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Ritmo y volumen</h2>
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          Lo que frena una campaña desbocada. Pasarse de aquí no da más capacidad: da
          llamadas rechazadas por la troncal.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Field label="Llamadas por segundo" hint="CPS máximo de la cuenta">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={1000}
              disabled={!editable}
              value={draft.max_cps}
              onChange={(e) => setDraft({ ...draft, max_cps: Number(e.target.value) })}
            />
          </Field>
          <Field label="Simultáneas" hint="llamadas a la vez">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={10000}
              disabled={!editable}
              value={draft.max_concurrent}
              onChange={(e) => setDraft({ ...draft, max_concurrent: Number(e.target.value) })}
            />
          </Field>
          <Field label="Duración máxima" hint="segundos por llamada">
            <input
              className={inputClass}
              type="number"
              min={10}
              max={14400}
              disabled={!editable}
              value={draft.max_call_seconds}
              onChange={(e) => setDraft({ ...draft, max_call_seconds: Number(e.target.value) })}
            />
          </Field>
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Horario permitido</h2>
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          En la zona de la cuenta ({timezone}). Fuera de la ventana no sale ninguna llamada.
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Field label="Desde">
            <input
              className={inputClass}
              type="time"
              disabled={!editable}
              value={minutesToTime(draft.allowed_from_minute)}
              onChange={(e) =>
                setDraft({ ...draft, allowed_from_minute: timeToMinutes(e.target.value) })
              }
            />
          </Field>
          <Field label="Hasta">
            <input
              className={inputClass}
              type="time"
              disabled={!editable}
              value={minutesToTime(draft.allowed_to_minute)}
              onChange={(e) =>
                setDraft({ ...draft, allowed_to_minute: timeToMinutes(e.target.value) })
              }
            />
          </Field>
          <p className="pb-2 text-xs text-slate-500">
            {draft.allowed_from_minute === draft.allowed_to_minute
              ? "Iguales: sin restricción de hora."
              : draft.allowed_from_minute > draft.allowed_to_minute
                ? "La ventana cruza la medianoche."
                : "Fuera de esta ventana no sale ninguna llamada."}
          </p>
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Destinos</h2>
        <div className="mt-4 max-w-xs">
          <Field label="Internacional" hint={`país de casa: +${draft.home_country_code}`}>
            <select
              className={inputClass}
              disabled={!editable}
              value={draft.allow_international ? "si" : "no"}
              onChange={(e) =>
                setDraft({ ...draft, allow_international: e.target.value === "si" })
              }
            >
              <option value="no">Deshabilitado</option>
              <option value="si">Habilitado</option>
            </select>
          </Field>
        </div>

        <div className="mt-4 text-xs">
          <p className="font-medium text-slate-300">Prefijos</p>
          <p className="mt-1 text-slate-500">
            Permitidos:{" "}
            {draft.prefix_allow.length === 0 ? (
              <em>todos salvo los denegados</em>
            ) : (
              <code>{draft.prefix_allow.join(", ")}</code>
            )}
            {" · "}
            Denegados:{" "}
            {draft.prefix_deny.length === 0 ? <em>ninguno</em> : <code>{draft.prefix_deny.join(", ")}</code>}
          </p>
          <p className="mt-1 text-slate-600">
            Lo denegado gana sobre lo permitido. Se editan desde la API por ahora.
          </p>
        </div>
      </Card>
    </div>
  );
}

/** SeccionAntifraude: qué se vigila y qué pasa cuando salta. */
function SeccionAntifraude({
  draft,
  setDraft,
  editable,
}: {
  draft: OutboundPolicy;
  setDraft: (p: OutboundPolicy) => void;
  editable: boolean;
}) {
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Qué se vigila</h2>
        <p className="mt-1 max-w-prose text-xs text-slate-500">
          Las dos señales de que una cuenta se salió de su patrón: muchas llamadas que no
          contestan, o de pronto muchas más de lo normal. Vacío significa no vigilar esa
          señal.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Ratio de fallidas" hint="% de llamadas sin contestar que dispara la alerta">
            <input
              className={inputClass}
              type="number"
              min={1}
              max={100}
              placeholder="sin vigilar"
              disabled={!editable}
              value={draft.max_failure_percent ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  max_failure_percent: e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
          </Field>
          <Field label="Factor de pico" hint="veces el ritmo habitual">
            <input
              className={inputClass}
              type="number"
              min={2}
              max={100}
              placeholder="sin vigilar"
              disabled={!editable}
              value={draft.spike_factor ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  spike_factor: e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
          </Field>
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-sm font-medium text-slate-100">Qué hacer cuando salta</h2>
        <div className="mt-4 max-w-sm">
          <Field label="Al detectar una anomalía">
            <select
              className={inputClass}
              disabled={!editable}
              value={draft.on_anomaly}
              onChange={(e) =>
                setDraft({ ...draft, on_anomaly: e.target.value as "alert" | "suspend" })
              }
            >
              <option value="alert">Avisar</option>
              <option value="suspend">Avisar y suspender</option>
            </select>
          </Field>
        </div>
        <p className="mt-3 max-w-prose text-xs text-amber-200/80">
          Suspender corta la cuenta sin que intervenga nadie: para el fraude de madrugada,
          pero también puede parar una campaña legítima que simplemente creció. Solo la
          plataforma la reactiva.
        </p>
      </Card>
    </div>
  );
}

function ExclusionList({
  exclusions,
  editable,
  onChanged,
}: {
  exclusions: Exclusion[];
  editable: boolean;
  onChanged: () => void;
}) {
  const [number, setNumber] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/api/v1/exclusions", { e164: number.trim(), reason: reason.trim() });
      setNumber("");
      setReason("");
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo añadir");
    } finally {
      setBusy(false);
    }
  }

  async function remove(e164: string) {
    setError(null);
    try {
      await api.delete(`/api/v1/exclusions/${encodeURIComponent(e164)}`);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo quitar");
    }
  }

  const columnas = useMemo<ColumnDef<Exclusion, any>[]>(
    () => [
      {
        id: "e164",
        accessorKey: "e164",
        header: "Número",
        meta: { label: "Número" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-100">{row.original.e164}</span>
        ),
      },
      {
        id: "reason",
        accessorKey: "reason",
        header: "Motivo",
        meta: { label: "Motivo" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{row.original.reason || "—"}</span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Cuándo",
        meta: { label: "Cuándo", hideOnMobile: true, width: "11rem" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
    ],
    [],
  );

  const acciones = useMemo<DataTableRowAction<Exclusion>[]>(
    () =>
      editable
        ? [{ label: "Quitar", tone: "danger", onSelect: (row) => void remove(row.e164) }]
        : [],
    [editable],
  );

  return (
    <section>
      <p className="mb-3 max-w-prose text-xs text-slate-500">
        Los números de esta lista no se marcan, y la API devuelve un rechazo genérico sin
        decir por qué: distinguirlo de otros bloqueos permitiría averiguar si un número
        concreto está aquí. Es <strong>tu</strong> lista, no una lista oficial.
      </p>

      {editable && (
        <Card className="mb-3 p-4">
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void add(e)}>
            <Field label="Número en E.164">
              <input
                className={inputClass}
                placeholder="+528180000000"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </Field>
            <Field label="Motivo (opcional)">
              <input
                className={inputClass}
                placeholder="lo pidió el abonado"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <Button type="submit" disabled={busy}>
              {busy ? "Añadiendo…" : "Añadir"}
            </Button>
          </form>
          {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
        </Card>
      )}

      <DataTable<Exclusion>
        data={exclusions}
        columns={columnas}
        getRowId={(row) => row.e164}
        caption="Lista de no-llamar"
        empty={{
          title: "Sin exclusiones",
          description: "No hay ningún número bloqueado para esta cuenta.",
        }}
        search={{ placeholder: "Buscar por número o motivo…" }}
        rowActions={acciones}
        // La lista entera viene en una respuesta: la tabla reparte las páginas.
        pagination="client"
        pageSize={25}
        urlStateKey="exclusiones"
      />
    </section>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "active") return <Badge tone="good">Activa</Badge>;
  if (status === "suspended") return <Badge tone="bad">Suspendida</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-100">{value}</dd>
    </div>
  );
}

function retention(days: number): string {
  return days === 0 ? "sin límite" : `${days} días`;
}

/** minutesToTime convierte minutos desde medianoche a `HH:MM`. */
function minutesToTime(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function timeToMinutes(value: string): number {
  // Un `<input type="time">` vacío manda cadena vacía, así que las dos partes
  // pueden faltar. Devolver 0 en ese caso es lo correcto: 0 y 0 significan
  // "sin restricción de hora".
  const parts = value.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}
